import { appState } from './state';
// Single source of truth for role ranking: operator < senior_operator < admin <
// superadmin. Mirrors the server's ROLE_RANK (server/auth.ts). Access checks compare
// ranks so a higher role automatically satisfies anything a lower role can, instead
// of `=== 'admin'`.
const ROLE_RANK = {
    operator: 0,
    senior_operator: 1,
    admin: 2,
    superadmin: 3,
};
export function rankOf(role) {
    return ROLE_RANK[role] ?? 0;
}
/**
 * UX mirror of the server rank policy: may the current user create/edit/delete a
 * target of role `targetRole`? True only when the target does not outrank them.
 * The server (server/users.ts) is the real guard — this just hides actions the
 * server would reject.
 */
export function canManageRole(targetRole) {
    return rankOf(currentRole()) >= rankOf(targetRole);
}
// Roles offered in the user form. 'superadmin' is assignable, but only to a
// superadmin: assignableRoles() filters by the current user's rank, so an admin
// still sees only operator/admin while a superadmin also sees superadmin. The
// id=1 DB migration (see server/db.ts) remains the BOOTSTRAP that guarantees an
// initial superadmin, no longer the only path. Mirrors the server's
// ASSIGNABLE_ROLES in server/users.ts. The form still appends a target's current
// role so editing never silently downgrades it.
const ASSIGNABLE_ROLES = ['operator', 'senior_operator', 'admin', 'superadmin'];
/** Roles the current user may assign — those at or below their own rank. */
export function assignableRoles() {
    const max = rankOf(currentRole());
    return ASSIGNABLE_ROLES.filter((r) => ROLE_RANK[r] <= max);
}
export const UI_ACCESS = {
    // Pages & navigation
    // "My Devices" — the operator's personal cross-farm dashboard. Every authenticated
    // user gets it: a workspace claim is a bookmark, not a privilege, and the server
    // scopes both the list and the mutations to what the caller can already reach
    // (GET /api/me/workspace is 'user'; the two mutations are 'device'-scoped).
    'page.workspace': 'operator',
    'nav.workspace': 'operator',
    'page.farms': 'operator',
    // Dashboards — the cross-farm reporting section (first tab: the operations registry).
    // Every authenticated user, operators included: the rank gates nothing here because
    // the handler is the authority and scopes every row through getAccessibleFarmIds +
    // the per-device whitelist. An operator therefore reads their own farms/devices, an
    // admin their team's, and only a superadmin (unrestricted everywhere in the panel)
    // reads across teams. Mirrors the 'user' level of GET /api/operations.
    'page.dashboards': 'operator',
    'nav.dashboards': 'operator',
    // Devices roster tab: every phone the caller may see, including dropped ones — admin+,
    // mirroring GET /api/devices/roster. Forgetting mirrors the admin-level DELETE route.
    'page.dashboards.devices': 'admin',
    // «Close shift»: anyone who may run the device's clicker (the 'farm' gate of POST …/close-shift).
    'clicker.closeShift': 'operator',
    'dashboards.devices.forget': 'admin',
    // Charts page — admin+, mirroring GET /api/charts/*.
    'page.charts': 'admin',
    'nav.charts': 'admin',
    // Clicker section — the sidebar item opens the Servers/Telegram/Code/Runner/Apps
    // tabs (all superadmin-gated server-side), so the page and its nav are superadmin
    // too. Per-farm clicker config lives in the farm settings "Clicker" tab instead
    // ('clicker.config').
    'page.clicker': 'superadmin',
    'nav.clicker': 'superadmin',
    // Users section — senior operators and above. A senior operator manages only
    // users at or below their own rank (operators + senior operators), scoped to
    // their own team; assignableRoles()/canManageRole() enforce that in the form.
    // The Teams tab + clicker-apps stay superadmin-only via their own keys below.
    'page.users': 'senior_operator',
    'nav.users': 'senior_operator',
    // Remote-ADB proxy page + its sidebar nav — superadmin-only (mirrors the
    // /api/adb-proxy/* routes' 'superadmin' level in server/index.ts). Opening a
    // device's ADB endpoint is full device root, so this is the most restricted page.
    'page.adbProxy': 'superadmin',
    // "Teams" tab inside the Users page — superadmin-only (first use of the
    // 'superadmin' level here; can() is rank-based so admins/operators fail it).
    'tab.teams': 'superadmin',
    // "API" tab inside the Users page — the team's integration API key plus the docs
    // for the endpoint it unlocks. Admin and above (mirrors the 'admin' level of
    // GET/PUT /api/teams/:id/api-key); the handlers additionally restrict a non-
    // superadmin to their OWN team, which the tab reflects by only ever listing the
    // teams GET /api/teams returns.
    'tab.teamApi': 'admin',
    // Clicker app catalog editor (Clicker settings → Apps tab) and the per-team
    // availability matrix on the team page — both superadmin-only (mirror the
    // POST/DELETE /api/clicker-apps and PUT /api/teams/:id/clicker-apps levels).
    // Reading the catalog/matrix for the launcher dropdown is 'user' and is NOT
    // gated by a key (the server's GET routes are 'user'/'farm').
    'clicker.apps': 'superadmin',
    // Flows — the panel-authored clicker flows section (its own page + sidebar item) and
    // the device element picker they are authored with. Superadmin-only, mirroring the
    // /api/clicker/payout-flows* and /api/devices/:udid/ui-dump route levels. Authoring a
    // flow only; POINTING a device at one is part of configuring that device's clicker and
    // is not gated here (clicker-flow-field.ts, GET /api/farms/:id/clicker/flows).
    'page.flows': 'superadmin',
    'nav.flows': 'superadmin',
    // One stored run, read-only (`#!/flows/op/<op>`) — the trajectory the registry's button
    // opens. Admin+, unlike the section above it: an admin reads it to see why their own
    // team's payout ended the way it did, and the run carries a single document that ALREADY
    // ran on their own team's device, not the registry of everyone's flows. The scope comes
    // from the operation, not from the flow: GET /api/operations/:id/run answers only for a
    // farm in the caller's accessible set, which for an admin is their team's farms.
    'page.flowRun': 'admin',
    // The registry's trajectory button — same level as the read-only page it opens.
    'ops.flowTrace': 'admin',
    // The registry's Team column: which team's farm an operation happened on. Only a
    // superadmin reads more than one team, so for anyone else the column would repeat
    // their own team's name down every row.
    'ops.team': 'superadmin',
    // Farms
    'farms.add': 'admin',
    'farm.settings': 'admin',
    // Devices-Access tab of farm settings — distributing a farm's devices to its
    // operators (the user_devices whitelist matrix). Senior operators and above.
    // Mirrors the 'farm'-scoped GET/PUT /api/farms/:id/access routes, which gate to
    // senior_operator+ in farmAccess.ts. Senior operators reach a settings page that
    // shows ONLY this tab (farm-settings-view gates every other tab on 'farm.settings').
    'farm.deviceAccess': 'senior_operator',
    'farm.rename': 'admin',
    // Remotely reboot a farm's host machine — admin and above (mirrors the
    // POST /api/farms/:id/reboot route's 'admin' level in server/index.ts).
    'farm.reboot': 'admin',
    // Restart a farm's agent container with an image update — admin and above.
    // The button builds the canonical docker recipe client-side (farm-docker-config)
    // and sends it to the shared POST /api/farms/:id/exec route ('admin' in
    // server/index.ts), so this level matches; there is no dedicated /update route.
    'farm.update': 'admin',
    // Remote shell terminal on the farm's host machine — admin and above (mirrors
    // the POST /api/farms/:id/exec route's 'admin' level in server/index.ts).
    'farm.exec': 'admin',
    // Clicker
    // The Clicker page's tabs (Servers/Telegram/Code/Runner) and clicker log viewing
    // are superadmin-only; per-farm config (farm settings "Clicker" tab) and the
    // running-aggregate stay admin.
    'clicker.settings': 'superadmin',
    'clicker.config': 'admin',
    'clicker.running': 'admin',
    // Per-device secret fields in the launch panel — PIN, App password and Bucket
    // code — visible/editable to any authenticated user (plain operators too). The
    // server no longer strips them by role in farmClicker.ts (handlePutFarmClickerDevice);
    // the route stays 'farm' so every operator can save these along with the other
    // per-device fields (task type, app).
    'clicker.secrets': 'operator',
    // Per-device Bucket code + Operators group + Telegram chats in the launch panel /
    // reconfigure modal. These are ROUTING, not secrets: they decide which queue the
    // device pulls tasks from and which chats its reports land in, so a wrong value
    // silently sends someone else's work (or reports) elsewhere. Admin and above only. The farm-wide pair on the Clicker page is already behind
    // 'clicker.config'. Mirrors the rank guard in handlePutFarmClickerDevice
    // (farmClicker.ts), which is the authority — a non-admin's save carries the stored
    // values forward instead of writing what it was sent.
    'clicker.routing': 'admin',
    // Stopping a clicker somebody ELSE started. A stop SIGTERMs a run that may be
    // mid-payout (the clicker closes the task it holds as cancelled), so a plain
    // operator may only stop their own start; senior operators and above may stop any.
    // Mirrors the guard on POST /api/farms/:id/clicker/devices/:udid/stop
    // (farmClicker.ts mayStopClicker), which is the authority — this only decides
    // whether the UI offers the Stop button. See canStopClicker in clicker-shared.
    'clicker.stopAny': 'senior_operator',
    // Devices
    'device.rename': 'admin',
    'device.apps': 'admin',
    // Delete/revoke a provisioned aggregator phone (the trash button on its card).
    // Admin and above only — mirrors the DELETE /api/farms/:id/aggregator-devices/:udid
    // route's 'admin' level in server/index.ts. Provisioning ("Add device") is already
    // admin-gated via 'farm.settings', so both add and delete stay admin+.
    'device.delete': 'admin',
    'device.install': 'operator',
    'device.shell': 'admin',
    // Restart phone / Power off phone commands in the device command popover —
    // admin and above only. Mirrors the per-command 'admin' guard on the reboot/
    // shutdown branch of POST /api/devices/:udid/command (server/index.ts).
    'device.reboot': 'admin',
    'device.clickerLog': 'superadmin',
    // Clicker run history — who started/stopped this device's clicker, when, and with
    // which launch params. Admin and above (mirrors the 'admin' level of
    // GET /api/farms/:id/clicker/devices/:udid/run-log in server/index.ts).
    'device.clickerHistory': 'admin',
    // Who keeps driving the screen while that device's clicker is running. Everyone
    // else gets the picture only: touch/keys/text/clipboard are muted and the
    // device-driving buttons are disabled, so a human tap cannot fight the automation
    // mid-transfer. Superadmin, deliberately narrower than the surrounding admin keys.
    // NOT a security boundary — the authority is the relay's own gate and the
    // device-command guard (server/index.ts, same 'superadmin' rank); keep the three
    // in step.
    'stream.clickerBypass': 'superadmin',
    'stream.kick': 'admin',
    'device.watch': 'senior_operator',
    // Screen-lock (Power) control button in the live stream toolbar — hidden from
    // plain operators; senior_operator and above only. UI-only (no server route:
    // Power is a keycode sent over the live control channel, not an API call).
    'device.power': 'senior_operator',
    // "Reset session" (re-plug) command in the device command popover — bounces the
    // device off the farm and back so the modem recovers after an MP-trigger, without a
    // physical power-cycle. Senior operators and above; mirrors the per-command
    // 'senior_operator' guard on POST /api/devices/:udid/command (server/index.ts).
    'device.session-reset': 'senior_operator',
    // Guest share links (any farm) — minting one hands the device's screen to
    // whoever holds the URL, with no panel session, so it sits at senior_operator+.
    // Mirrors the 'device' level of /api/farms/:id/devices/:udid/shares, which is
    // access-scoped but not rank-gated: this key is the rank gate.
    'device.share': 'senior_operator',
    // Cloud-phone ADB enable/disable toggle on geelark cards. Distinct from 'device.power'
    // above (aggregator screen-lock keycode): toggling ADB is device management, so it sits
    // at admin. Mirrors the 'device' + minRole 'admin' guard on the geelark adb route.
    'device.cloudAdb': 'admin',
    // Power on/off button on a VMOS instance card (docker start/stop of the marsbox
    // container behind it). Same class of action as "Restart / Power off phone", so it
    // sits at the same rank and mirrors the per-command 'admin' guard on the
    // vmos-power-on / vmos-power-off branch of POST /api/devices/:udid/command.
    'device.vmosPower': 'admin',
};
export function currentRole() {
    const r = appState.currentUserRole;
    return r === 'superadmin' || r === 'admin' || r === 'senior_operator' ? r : 'operator';
}
/** True for admins and anything ranked above them (currently superadmin). */
export function isAdmin() {
    return rankOf(currentRole()) >= ROLE_RANK.admin;
}
/** Whether the current user's role is allowed to see the given UI element. */
export function can(capability) {
    const required = UI_ACCESS[capability];
    return rankOf(currentRole()) >= rankOf(required);
}
